import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import BreadCrumb from "../Controls/BreadCrumb";
import UserData from "../Controls/UserData";
import { Form, FormInput, FormActions, FormCheckbox, FormSelect, FormSelectList, FormTextArea, FormDate } from "../Controls/Form";
import Help from "../Controls/Help";
import EmploymentSelectionListItem from "../ListItems/EmploymentSelectionListItem";
import SkillSelectionListItem from "../ListItems/SkillSelectionListItem";
import EmploymentTypeSelectionListItem from "../ListItems/EmploymentTypeSelectionListItem";
import { ImNewspaper } from "react-icons/im";
import { toast } from "react-toastify";
import { queryKeys } from "../../query/keys";
import { refreshCareers } from "../../query/useCareers";
import { useEmploymentQuery } from "../../query/useEmployment";
import { useCareersQuery } from "../../query/useCareers";

function emptyCareer() {
  return {
    ID: 0,
    id: 0,
    name: "",
    Name: "",
    objective: "",
    Objective: "",
    openToPerm: true,
    OpenToPerm: true,
    openToContract: false,
    OpenToContract: false,
    openToRelocation: false,
    OpenToRelocation: false,
    maxTravelPercent: 0,
    MaxTravelPercent: 0,
    interestLevelID: 50,
    InterestLevelID: 50,
    nextAvailableDate: null,
    NextAvailableDate: null,
    isEarlyCareer: false,
    IsEarlyCareer: false,
    publicationsSummary: "",
    PublicationsSummary: "",
    resumeFormat: 1,
    ResumeFormat: 1,
    employments: [],
    Employments: [],
    skills: [],
    Skills: [],
    employmentTypes: [],
    EmploymentTypes: [],
  };
}

function normalizeCareer(careerObj) {
  if (!careerObj) return emptyCareer();
  return {
    ...careerObj,
    id: careerObj.ID || careerObj.id || 0,
    ID: careerObj.ID || careerObj.id || 0,
    name: careerObj.Name || careerObj.name || "",
    Name: careerObj.Name || careerObj.name || "",
    objective: careerObj.Objective || careerObj.objective || "",
    Objective: careerObj.Objective || careerObj.objective || "",
    openToPerm: careerObj.OpenToPerm ?? careerObj.openToPerm ?? false,
    OpenToPerm: careerObj.OpenToPerm ?? careerObj.openToPerm ?? false,
    openToContract: careerObj.OpenToContract ?? careerObj.openToContract ?? false,
    OpenToContract: careerObj.OpenToContract ?? careerObj.openToContract ?? false,
    openToRelocation: careerObj.OpenToRelocation ?? careerObj.openToRelocation ?? false,
    OpenToRelocation: careerObj.OpenToRelocation ?? careerObj.openToRelocation ?? false,
    maxTravelPercent: careerObj.MaxTravelPercent ?? careerObj.maxTravelPercent ?? 0,
    MaxTravelPercent: careerObj.MaxTravelPercent ?? careerObj.maxTravelPercent ?? 0,
    interestLevelID: careerObj.InterestLevelID ?? careerObj.interestLevelID ?? 50,
    InterestLevelID: careerObj.InterestLevelID ?? careerObj.interestLevelID ?? 50,
    nextAvailableDate: careerObj.NextAvailableDate ?? careerObj.nextAvailableDate ?? null,
    NextAvailableDate: careerObj.NextAvailableDate ?? careerObj.nextAvailableDate ?? null,
    isEarlyCareer: careerObj.IsEarlyCareer ?? careerObj.isEarlyCareer ?? false,
    IsEarlyCareer: careerObj.IsEarlyCareer ?? careerObj.isEarlyCareer ?? false,
    publicationsSummary: careerObj.PublicationsSummary ?? careerObj.publicationsSummary ?? "",
    PublicationsSummary: careerObj.PublicationsSummary ?? careerObj.publicationsSummary ?? "",
    resumeFormat: careerObj.ResumeFormat ?? careerObj.resumeFormat ?? 1,
    ResumeFormat: careerObj.ResumeFormat ?? careerObj.resumeFormat ?? 1,
    employments: careerObj.Employments || careerObj.employments || [],
    skills: careerObj.Skills || careerObj.skills || [],
    employmentTypes: careerObj.EmploymentTypes || careerObj.employmentTypes || [],
  };
}

export default function CareerEdit(props) {
  const { id: idStr } = useParams();
  const id = Number.parseInt(idStr, 10);
  const isCreate = !Number.isFinite(id) || id <= 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { onDone } = props;
  const lookups = UserData.Lookups() || {};
  const { data: careersData } = useCareersQuery();
  const { data: employmentData } = useEmploymentQuery();
  const employmentHistory = employmentData?.employmentHistory || [];
  const catalogSkills = (careersData?.skills || []).slice().sort((a, b) => {
    return (a.Name || a.name) < (b.Name || b.name) ? -1 : 1;
  });

  const [career, updateCareer] = useState(() => {
    if (isCreate) return emptyCareer();
    const list = careersData?.careers || [];
    const found = list.find((c) => c.ID === id || c.id === id);
    return normalizeCareer(found);
  });

  useEffect(() => {
    if (isCreate || !careersData?.careers) return;
    const found = careersData.careers.find((c) => c.ID === id || c.id === id);
    if (found) updateCareer(normalizeCareer(found));
  }, [careersData?.careers, id, isCreate]);

  const [allEmployments, setAllEmployments] = useState(() => {
    if (career && (career.employments || []).length > 0) {
      return career.employments.length === employmentHistory.length;
    }
    return false;
  });

  const [allSkills, setAllSkills] = useState(() => career?.skills || []);
  const skillList = catalogSkills;

  useEffect(() => {
    if (career && career.id) {
      if (career.employments && career.employments.length > 0) {
        setAllEmployments(career.employments.length === employmentHistory.length);
      }
      if (career.skills && career.skills.length > 0) {
        setAllSkills(career.skills);
      }
    }
  }, [career, employmentHistory]);

  const travelPercentOptions = [];
  for (let i = 0; i <= 100; i += 10) travelPercentOptions.push({ value: i, label: i + "%" });

  const isEmploymentSelected = (empId) => ((career || {}).employments || []).includes(empId);
  const isSkillSelected = (skillId) => ((career || {}).skills || []).includes(skillId);

  const onChangeTravelPercent = (newValue) => {
    const value = newValue?.value ?? newValue;
    updateCareer({
      ...career,
      maxTravelPercent: value,
      MaxTravelPercent: value,
    });
  };

  const onChangeInterestLevel = (newValue) => {
    const value = newValue?.value ?? newValue;
    updateCareer({
      ...career,
      interestLevelID: value,
      InterestLevelID: value,
    });
  };

  const onChangeResumeFormat = (newValue) => {
    const value = Number(newValue?.value ?? newValue?.Value ?? newValue);
    updateCareer({
      ...career,
      resumeFormat: value,
      ResumeFormat: value,
    });
  };

  async function onDoneAsync() {
    const payload = {
      ...career,
      ID: career.ID || career.id || 0,
      Name: career.name || career.Name || "",
      Objective: career.objective || career.Objective || "",
      OpenToPerm: career.openToPerm ?? career.OpenToPerm,
      OpenToContract: career.openToContract ?? career.OpenToContract,
      OpenToRelocation: career.openToRelocation ?? career.OpenToRelocation,
      MaxTravelPercent: career.maxTravelPercent ?? career.MaxTravelPercent ?? 0,
      InterestLevelID: career.interestLevelID ?? career.InterestLevelID ?? 50,
      NextAvailableDate: career.NextAvailableDate ?? career.nextAvailableDate ?? null,
      IsEarlyCareer: career.isEarlyCareer ?? career.IsEarlyCareer ?? false,
      PublicationsSummary: career.publicationsSummary ?? career.PublicationsSummary ?? "",
      ResumeFormat: Number(career.resumeFormat ?? career.ResumeFormat ?? 1),
      Employments: career.employments || career.Employments || [],
      Skills: career.skills || career.Skills || [],
      EmploymentTypes: career.employmentTypes || career.EmploymentTypes || [],
    };

    try {
      const res = await UserData.UpdateAsync("careers", payload, true);
      if (res && res.ok) {
        toast.success("Career saved");
        const raised =
          res.saveResp?.jsonData?.RaisedToLookingNow === true ||
          res.saveResp?.jsonData?.raisedToLookingNow === true ||
          res.jsonData?.RaisedToLookingNow === true;
        if (raised) {
          toast.info(
            "Looking now: keep skills and prefs current — CareerMaster will match you. Check the dashboard for coaching tips."
          );
        }
        await refreshCareers(queryClient);
        const careerId =
          Number(res.saveResp?.jsonData?.ID || res.saveResp?.jsonData?.id || payload.ID || id) || 0;
        if (careerId > 0) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.resumePresentation(careerId) });
        }
        if (onDone) onDone();
        else navigate("/careers");
      } else {
        toast.error(res?.message || "Failed to save career");
      }
    } catch (reason) {
      toast.error("Error: " + reason);
      console.error("Failed to update career: " + reason);
    }
  }

  const onToggleAllEmployments = () => {
    updateCareer({
      ...career,
      employments: !allEmployments ? employmentHistory.map((eh) => eh.ID) : [],
    });
    setAllEmployments(!allEmployments);
  };

  const onToggleAllSkills = () => {
    updateCareer({
      ...career,
      skills: !allSkills.length ? skillList.map((s) => s.ID) : [],
    });
    setAllSkills(!allSkills.length ? skillList.map((s) => s.ID) : []);
  };

  const onToggleAllEmploymentTypes = () => {
    const allEmploymentTypeKeys = (lookups.employmentType || []).map((et) => et.Key);
    const allSelected =
      (lookups.employmentType || []).length === (career.employmentTypes || []).length &&
      (lookups.employmentType || []).every((et) => (career.employmentTypes || []).includes(et.Key));
    updateCareer({
      ...career,
      employmentTypes: allSelected ? [] : allEmploymentTypeKeys,
    });
  };

  const isEmploymentTypeSelected = (typeId) => {
    if (!career || !career.employmentTypes) return false;
    return career.employmentTypes.includes(typeId);
  };

  const Return = () => navigate("/careers");

  const onDelete = async () => {
    if (!(career.id > 0)) return;
    await UserData.DeleteAsync("careers", career.id);
    await refreshCareers(queryClient);
    toast.info("Career deleted.");
    Return();
  };

  const interestLevels = (lookups.employmentInterestLevel || []).map((il) => {
    return { Key: il.Value, Value: il.Key };
  });
  const resumeFormats = (lookups.resumeFormat || []).map((rf) => {
    return { Key: rf.Value, Value: rf.Key };
  });

  return (
    <div className="pagebody">
      <BreadCrumb
        props={[{ route: "/careers", label: "Careers" }, career.name || (isCreate ? "New career" : "Career")]}
      />
      <div className="tabbody">
        <Form object={career} onUpdate={updateCareer}>
          <span className="pageTitle">Career - {career.name || "New"}</span>
          <Help id="2137" />
          {career.id > 0 ? (
            <Link to={"/resume/" + career.id} target="_blank" className="" title="View Resume">
              <ImNewspaper size={1} color="red" />
            </Link>
          ) : null}
          <FormInput field="name" label="Name" required />
          <FormTextArea field="objective" label="Objective" />
          <FormCheckbox label="Open to permanent placement" field="openToPerm" />
          <FormCheckbox label="Open to contract" field="openToContract" />
          <FormCheckbox label="Open to relocation" field="openToRelocation" />
          <FormSelect
            label="Max Travel Percent"
            field="maxTravelPercent"
            valueProp="value"
            labelProp="label"
            options={travelPercentOptions}
            onChange={onChangeTravelPercent}
          />
          <FormSelect
            label="Interest Level"
            field="interestLevelID"
            valueProp="Value"
            labelProp="Key"
            options={interestLevels}
            onChange={onChangeInterestLevel}
            helpid=""
          />
          <FormSelect
            label="Resume format"
            field="resumeFormat"
            valueProp="Value"
            labelProp="Key"
            options={resumeFormats.length ? resumeFormats : [
              { Key: "Classic", Value: 1 },
              { Key: "Chronological", Value: 2 },
              { Key: "SkillsFirst", Value: 3 },
            ]}
            onChange={onChangeResumeFormat}
          />
          <FormDate
            field="NextAvailableDate"
            label="Available starting"
            helpid="2140"
          />
          <p className="fieldHint">
            Leave blank if you are available now. Used when matching against a job&apos;s start date.
          </p>
          <FormCheckbox label="Early career / new graduate" field="isEarlyCareer" />
          <p className="fieldHint">
            Signals early-career matching (classes, grades, papers, internships). Internships belong in
            employment history.
          </p>
          <FormTextArea field="publicationsSummary" label="Papers / publications (optional)" />
          <FormSelectList
            field="employmentTypes"
            label={
              "Select the employment types you are interested in: (" +
              (career && career.employmentTypes ? career.employmentTypes.length : 0) +
              " selected of " +
              (lookups.employmentType || []).length +
              ")"
            }
            options={lookups.employmentType || []}
            onToggleSelect={() => onToggleAllEmploymentTypes()}
            renderItem={(employmentType, employmentTypeOnClick) => {
              return (
                <EmploymentTypeSelectionListItem
                  key={employmentType.Key}
                  employmentType={employmentType}
                  employmentTypes={lookups.employmentType}
                  selected={isEmploymentTypeSelected(employmentType.Key)}
                  onClick={employmentTypeOnClick}
                />
              );
            }}
          />
          <FormSelectList
            field="employments"
            label={
              "Select the jobs that should be included in this career path: (" +
              (career.employments || []).length +
              " selected of " +
              employmentHistory.length +
              ")"
            }
            options={employmentHistory}
            onToggleSelect={() => onToggleAllEmployments()}
            renderItem={(employment, parentOnClick) => {
              return (
                <EmploymentSelectionListItem
                  key={employment.ID}
                  employment={employment}
                  employers={employmentData?.employers || []}
                  selected={isEmploymentSelected(employment.ID)}
                  onClick={parentOnClick}
                />
              );
            }}
          />
          <FormSelectList
            field="skills"
            label={
              "Select the skills that should be highlighted in this career path: (" +
              (career.skills || []).length +
              " selected of " +
              (skillList || []).length +
              ")"
            }
            options={skillList}
            onToggleSelect={() => onToggleAllSkills()}
            renderItem={(skill, parentOnClick) => {
              return (
                <SkillSelectionListItem
                  key={skill.ID}
                  skill={skill}
                  selected={isSkillSelected(skill.ID)}
                  onClick={parentOnClick}
                />
              );
            }}
          />

          <FormActions
            onSaveAsync={async () => {
              await onDoneAsync();
            }}
            onCancelAsync={async () => {
              Return();
            }}
            onDeleteAsync={
              career.id > 0
                ? async () => {
                    await onDelete();
                  }
                : undefined
            }
          />
        </Form>
      </div>
    </div>
  );
}
